/* eslint-disable no-param-reassign */
import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Row } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PARTNER } from '../../../../constants/define/translation';
import {
  postCreatePartnerRepository,
  putUpdatePartnerRepository,
} from '../../../../repository/mytm/partner/partnerRepository';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { FormPartnerType } from '../../../../types/benefit';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SpinLoading } from '../../../atoms';
import { PreviewPartner } from '../../../molecules/preview/common';
import { DragDropImage } from '../../../molecules/upload';

interface FormPartnerProps {
  record?: FormPartnerType;
  onRefresh: () => void;
  onReFreshBenefit: () => void;
  onRefreshPartnerAll: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
}
const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;

export const FormPartner = (props: FormPartnerProps) => {
  const { t } = useTranslation(PARTNER);
  const { record, onRefresh, onReFreshBenefit, onRefreshPartnerAll, onCloseModal, setDataRecord } = props;
  const [form] = Form.useForm();
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState({});

  const defaultValue = {
    title: record?.title,
    imageUrl: record?.partnerBrandImageUrl,
  };

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onChangeInput = useCallback(
    e => {
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
    },
    [form],
  );

  const onFinish = useCallback(
    (formValue: FormPartnerType | any) => {
      if (!imageList.length) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      const imgList = imageList.filter(img => !img.isImageURL);
      const imgLink = imageList.filter(img => img.isImageURL);
      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const imageUrl = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];

          const payloadToPost = {
            ...formValue,
            partnerBrandImageUrl: imageUrl,
          };
          if (record) {
            return putUpdatePartnerRepository({
              ...payloadToPost,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onReFreshBenefit();
                onRefreshPartnerAll();
                onCloseModal();
                message.success(t('message.success.updatePartner'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreatePartnerRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onRefreshPartnerAll();
              onCloseModal();
              message.success(t('message.success.createPartner'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList],
  );

  useEffect(() => {
    if (record) {
      setImageList([{ dataURL: record?.partnerBrandImageUrl, isImageURL: true }]);
      setSelectedImage(0);
      setDataForm(record);
    }
  }, [record]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-Partner">
            <Row className="form-Partner-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImage
                  {...{
                    imageList,
                    onSetImageList,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewPartner
                        values={{
                          ...dataForm,
                          backgroundImage: imageList?.length && imageList[0].dataURL,
                        }}
                        isPreview
                        height="200px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormPartner span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdatePartner') : t('form.btnCreatePartner')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormPartner>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & .form-Partner-container {
    padding: 1.5rem;

    .form-Partner__description {
      resize: none;
    }
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormPartner = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }
`;
